import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { CompanyApiService, CompanyInactivitySettingsModel } from './api-services';
import { AuthenticationService } from './authentication.service';
import { SessionExpiringDialogComponent } from '../../shared/presentationals/session-expiring-dialog/session-expiring-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class InactivityService implements OnDestroy {
  private settings: CompanyInactivitySettingsModel | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private activitySubscription: Subscription | null = null;
  private visibilitySubscription: Subscription | null = null;
  private storageSubscription: Subscription | null = null;
  private authSubscription: Subscription | null = null;
  private dialogRef: MatDialogRef<SessionExpiringDialogComponent> | null = null;

  private logoutDeadline: number | null = null;
  private readonly STORAGE_KEY = 'comitas_session_deadline';

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly companyApiService: CompanyApiService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly ngZone: NgZone
  ) { }

  public initialize(): void {
    this.authSubscription = this.authenticationService.authenticated$
      .subscribe(auth => {
        if (auth && !this.authenticationService.getHasToMfa()) {
          this.loadSettingsAndStart();
        } else {
          this.stop();
        }
      });
  }

  private loadSettingsAndStart(): void {
    this.companyApiService.getInactivitySettings()
      .pipe(take(1))
      .subscribe({
        next: (settings) => {
          this.settings = settings;

          this.startTracking();
        },
        error: () => {
          this.settings = {
            inactivityTimeoutInMinutes: 1440,
            showInactivityWarning: true,
            inactivityWarningLeadTimeInMinutes: 2
          };
          this.startTracking();
        }
      });
  }

  private startTracking(): void {
    this.stop();

    if (!this.settings) {
      return;
    }

    const storedDeadline = localStorage.getItem(this.STORAGE_KEY);
    if (storedDeadline) {
      const deadline = parseInt(storedDeadline, 10);
      if (Date.now() >= deadline) {
        localStorage.removeItem(this.STORAGE_KEY);
        this.performLogout();
        return;
      }
      this.logoutDeadline = deadline;
    } else {
      const totalTimeoutMs = this.settings.inactivityTimeoutInMinutes * 60 * 1000;
      this.logoutDeadline = Date.now() + totalTimeoutMs;
      localStorage.setItem(this.STORAGE_KEY, this.logoutDeadline.toString());
    }

    const activityEvents$ = merge(
      fromEvent(document, 'click'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'wheel')
    ).pipe(debounceTime(300));

    this.activitySubscription = activityEvents$.subscribe(() => {
      this.resetTimer();
    });

    this.visibilitySubscription = fromEvent(document, 'visibilitychange').subscribe(() => {
      if (document.visibilityState === 'visible') {
        this.ngZone.run(() => this.checkDeadlineOnReturn());
      }
    });

    this.storageSubscription = fromEvent<StorageEvent>(window, 'storage').subscribe((event) => {
      if (event.key === this.STORAGE_KEY) {
        this.ngZone.run(() => {
          if (event.newValue) {
            this.logoutDeadline = parseInt(event.newValue, 10);
            if (this.dialogRef) {
              this.dialogRef.close('stay');
              this.dialogRef = null;
            }
            this.scheduleTimers();
          } else {
            this.performLogout();
          }
        });
      }
    });

    this.scheduleTimers();
  }

  private scheduleTimers(): void {
    if (!this.settings || !this.logoutDeadline) return;

    this.clearTimer();

    const now = Date.now();
    const remainingMs = this.logoutDeadline - now;

    if (remainingMs <= 0) {
      this.performLogout();
      return;
    }

    const warningLeadTimeMs = this.settings.showInactivityWarning
      ? this.settings.inactivityWarningLeadTimeInMinutes * 60 * 1000
      : 0;
    const remainingWarningMs = remainingMs - warningLeadTimeMs;

    if (this.settings.showInactivityWarning && warningLeadTimeMs > 0) {
      if (remainingWarningMs > 0) {
        this.ngZone.runOutsideAngular(() => {
          this.idleTimer = setTimeout(() => {
            this.ngZone.run(() => {
              if (this.syncDeadlineFromStorage()) return;
              this.showWarningDialog();
            });
          }, remainingWarningMs);
        });
      } else {
        if (!this.dialogRef) {
          this.showWarningDialog();
        }
      }
    } else {
      this.ngZone.runOutsideAngular(() => {
        this.idleTimer = setTimeout(() => {
          this.ngZone.run(() => {
            if (this.syncDeadlineFromStorage()) return;
            this.performLogout();
          });
        }, remainingMs);
      });
    }
  }

  public resetTimer(): void {
    if (!this.settings) return;

    if (this.dialogRef) {
      this.dialogRef.close('stay');
      this.dialogRef = null;
    }

    const totalTimeoutMs = this.settings.inactivityTimeoutInMinutes * 60 * 1000;
    this.logoutDeadline = Date.now() + totalTimeoutMs;
    localStorage.setItem(this.STORAGE_KEY, this.logoutDeadline.toString());

    this.scheduleTimers();
  }

  private checkDeadlineOnReturn(): void {
    const storedDeadline = localStorage.getItem(this.STORAGE_KEY);
    if (storedDeadline) {
      this.logoutDeadline = parseInt(storedDeadline, 10);
    }
    this.scheduleTimers();
  }

  private syncDeadlineFromStorage(): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      const freshDeadline = parseInt(stored, 10);
      if (freshDeadline > (this.logoutDeadline ?? 0)) {
        this.logoutDeadline = freshDeadline;
        this.scheduleTimers();
        return true;
      }
    }
    return false;
  }

  private showWarningDialog(): void {
    if (this.dialogRef) {
      return;
    }

    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }

    this.dialogRef = this.dialog.open(SessionExpiringDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        logoutDeadline: this.logoutDeadline!
      }
    });

    this.dialogRef.afterClosed().subscribe((result: string) => {
      this.dialogRef = null;
      if (result === 'stay') {
        const totalTimeoutMs = this.settings!.inactivityTimeoutInMinutes * 60 * 1000;
        const newDeadline = Date.now() + totalTimeoutMs;
        localStorage.setItem(this.STORAGE_KEY, newDeadline.toString());
        this.startTracking();
      } else {
        this.performLogout();
      }
    });
  }

  private performLogout(): void {
    this.stop();
    localStorage.removeItem(this.STORAGE_KEY);
    this.authenticationService.logout()
      .pipe(take(1))
      .subscribe(() => {
        this.router.navigate([this.authenticationService.getLoginUrl()]);
      });
  }

  private clearTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private stop(): void {
    this.clearTimer();
    this.logoutDeadline = null;
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
    if (this.visibilitySubscription) {
      this.visibilitySubscription.unsubscribe();
      this.visibilitySubscription = null;
    }
    if (this.storageSubscription) {
      this.storageSubscription.unsubscribe();
      this.storageSubscription = null;
    }
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }
}
